import { Button, Group, Stack } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table"
import { useMemo } from "react"

import {
  JobExecutionsResponses,
  listJobsZod,
} from "@costory/types/endpoints/jobExecutions"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"
import { useJobCreation } from "@costory/front/queries/jobExecutions"
import { getDefaultTableOptions } from "@costory/front/utils/table"

type TYPE_UPDATE = "update" | "full_refresh"
type Props = {
  insights: JobExecutionsResponses.ListJobs[]
  type: TYPE_UPDATE
}

const JobExecutions = ({ insights, type }: Props) => {
  const { mutateAsync: createUpdateJob, isPending: isPendingCreation } =
    useJobCreation()
  const columns = useMemo(() => {
    const columnHelper =
      createMRTColumnHelper<JobExecutionsResponses.ListJobs>()
    return [
      columnHelper.accessor("startedAt", {
        header: "Started At",
        Cell: ({ cell }) => (
          <span>
            {cell.row.original.startedAt
              ? cell.row.original.startedAt.toLocaleString()
              : ""}
          </span>
        ),
      }),
      columnHelper.accessor("endedAt", {
        header: "Ended At",
        Cell: ({ cell }) => <span>{cell.getValue()?.toLocaleString()}</span>,
      }),
      columnHelper.accessor("state", {
        header: "Status",
        filterVariant: "select",
      }),
    ]
  }, [])

  const table = useMantineReactTable<JobExecutionsResponses.ListJobs>({
    columns,
    data: insights,
    ...getDefaultTableOptions(),
  })

  return (
    <Stack>
      <Group justify="flex-end">
        <Group>
          <Button
            loading={isPendingCreation}
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["jobExecutions"] })
            }
            data-intercom-target="costory-tour-vdim-refresh-jobs"
          >
            Refresh
          </Button>
          <Button
            loading={isPendingCreation}
            onClick={() => createUpdateJob(type)}
            data-intercom-target="costory-tour-vdim-create-job"
          >
            Create {type === "full_refresh" ? "full refresh" : " update "} Job
          </Button>
        </Group>
      </Group>

      <MantineReactTable table={table} />
    </Stack>
  )
}

export const JobExecutionsPage = ({ type }: { type: TYPE_UPDATE }) => {
  const insightsQuery = useQuery({
    queryKey: ["jobExecutions", type],
    queryFn: async () => {
      const response = await apiClient.get<JobExecutionsResponses.ListJobs[]>(
        `/job-executions/${type}`,
      )
      return listJobsZod.safeParse(response.data).data
    },
  })

  return (
    <QueryWrapper query={insightsQuery} allowEmptyArray>
      {({ data }) => <JobExecutions type={type} insights={data || []} />}
    </QueryWrapper>
  )
}
